import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import addDays from 'date-fns/addDays';
import isAfter from 'date-fns/isAfter';
import isToday from 'date-fns/isToday';
import { useEffect, useMemo } from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';

import { useAccessToken } from 'reservations/account/providers/AccessTokenProvider';
import { useBookingConfig } from 'reservations/account/providers/ConfigProvider';
import { trackTimeSlotSelection } from 'reservations/analytics/helpers';
import AllTimeSlots from 'reservations/availabilities/components/AllTimeSlots';
import ClosestAvailableTimeSlots from 'reservations/availabilities/components/ClosestAvailableTimeSlots';
import { NoAvailabilities } from 'reservations/availabilities/components/NoAvailabilities';
import { OutsideAvailabilityWindow } from 'reservations/availabilities/components/OutsideAvailabilityWindow';
import { ANYTIME } from 'reservations/availabilities/constants';
import { useAvailabilitiesContext } from 'reservations/availabilities/providers/AvailabilitiesProvider';
import { useFirstAvailableDate } from 'reservations/availabilities/providers/FirstAvailableDateProvider';
import { useAvailabilitiesQuery } from 'reservations/availabilities/queries/useAvailabilitiesQuery';
import type { Slot } from 'reservations/availabilities/types';
import LoadingSpinner from 'reservations/base/components/LoadingSpinner';
import SystemError from 'reservations/base/components/SystemError';
import { COMMON_DATETIME_FORMATS } from 'reservations/base/helpers/datetime';
import {
  FORM_DRAWER,
  useModal
} from 'reservations/base/providers/ModalProvider';
import { RESERVATION, WAITPARTY } from 'reservations/booking/types';
import { DinerFieldsDrawer } from 'reservations/form/components/DinerFieldsDrawer';
import {
  SUBMISSION_TYPES,
  useBookingForm
} from 'reservations/form/providers/BookingFormProvider';
import JoinWaitlistButton from 'reservations/waitlist/components/JoinWaitlistButton';
import { useWaitlist } from 'reservations/waitlist/providers/WaitlistProvider';

function Availabilities() {
  const { hasActiveOnlineWaitlist } = useWaitlist();
  const { handleSetTimePickerSlot } = useAvailabilitiesContext();
  const {
    handleModalClose,
    handleModalOpen: handleFormDrawerOpen,
    isModalOpen: isFormDrawerOpen
  } = useModal(FORM_DRAWER);
  const { maxAdvanceDays } = useBookingConfig();
  const { slug, access_token: apiKey } = useAccessToken();
  const {
    field: { onChange, name }
  } = useController({
    name: 'availability'
  });
  const { setValue } = useFormContext();

  const [partySize, date, time] = useWatch({
    name: ['partySize', 'date', 'time']
  });

  const { hasFirstAvailableDate } = useFirstAvailableDate();

  const formattedDateString = useMemo(
    () => COMMON_DATETIME_FORMATS.YMMdd(date),
    [date]
  );
  const { setSubmissionType, submissionType } = useBookingForm();
  const { isCreate, isWaitlist, isModify } = useBookingForm();
  const isCreateBookingOrWaitlist = useMemo(
    () => isCreate || isWaitlist,
    [isCreate, isWaitlist]
  );

  const showJoinWaitlistButton = useMemo(
    () => isToday(date) && isCreateBookingOrWaitlist && hasActiveOnlineWaitlist,
    [date, hasActiveOnlineWaitlist, isCreateBookingOrWaitlist]
  );

  const showJoinWaitlistButtonWithAvailabilities = useMemo(
    () => time === ANYTIME && showJoinWaitlistButton,
    [time, showJoinWaitlistButton]
  );

  const { data, isPending, isError } = useAvailabilitiesQuery({
    partySize,
    slug,
    apiKey,
    date: formattedDateString
  });

  useEffect(() => {
    if (!data?.isEmpty) {
      const timePickerSlots = data?.slots
        .map((slot: Slot | null, index: number) => ({
          index,
          time: slot?.time12
        }))
        .filter(slot => slot?.time != null);

      if (timePickerSlots != null) {
        handleSetTimePickerSlot(timePickerSlots);
      }
    } else {
      handleSetTimePickerSlot([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.isEmpty]);

  // istanbul ignore next
  const handleSlotClick = (timeSlot: string) => {
    setSubmissionType(
      isModify ? SUBMISSION_TYPES.MODIFY_FORM : SUBMISSION_TYPES.CREATE_FORM
    );
    setValue('bookingType', RESERVATION);
    onChange(timeSlot);
    trackTimeSlotSelection(submissionType, timeSlot);
    handleFormDrawerOpen();
  };

  const handleWaitlistClick = () => {
    setSubmissionType(SUBMISSION_TYPES.WAITLIST_FORM);
    setValue('bookingType', WAITPARTY);
    handleFormDrawerOpen();
  };

  if (isPending) {
    return (
      <Box sx={{ py: '16px' }}>
        <LoadingSpinner message="Loading Reservations..." />
      </Box>
    );
  }

  // istanbul ignore next
  if (isError) {
    return (
      <Box sx={{ p: '32px' }}>
        <SystemError />
      </Box>
    );
  }

  const todaysDate = new Date();
  const lastDayToMakeReservation = addDays(todaysDate, maxAdvanceDays);

  if (isAfter(date, lastDayToMakeReservation)) {
    return (
      <OutsideAvailabilityWindow
        date={date}
        maxAdvanceDays={maxAdvanceDays}
        lastDayToMakeReservation={lastDayToMakeReservation}
      />
    );
  }

  if (data.isEmpty && showJoinWaitlistButton) {
    return (
      <Grid container direction="column" gap="36px">
        <Grid item sx={{ px: '16px' }}>
          <Typography
            sx={{
              fontSize: '16px',
              textAlign: 'center'
            }}
          >
            We don&apos;t have any more reservations today, but we do hold
            tables for walk-ins.
          </Typography>
        </Grid>
        <Grid item sx={{ px: '16px' }}>
          <JoinWaitlistButton
            showNumberOfPartiesAhead
            sxProps={{ maxWidth: '95%' }}
            onClick={handleWaitlistClick}
          />
        </Grid>
        <DinerFieldsDrawer
          open={isFormDrawerOpen}
          handleFormDrawerClose={handleModalClose}
        />
      </Grid>
    );
  }

  if (data.isEmpty) {
    return (
      <NoAvailabilities
        date={date}
        partySize={partySize}
        hasFirstAvailableDate={hasFirstAvailableDate}
        maxAdvanceDays={maxAdvanceDays}
      />
    );
  }

  return (
    <Grid
      container
      direction="column"
      gap="24px"
      data-testid="availabilities-container"
    >
      {showJoinWaitlistButtonWithAvailabilities && (
        <Grid item sx={{ px: '16px' }}>
          <JoinWaitlistButton
            variant="outlined"
            showNumberOfPartiesAhead
            onClick={handleWaitlistClick}
          />
        </Grid>
      )}
      <Grid
        item
        container
        alignContent="center"
        justifyContent="center"
        spacing="4px"
        sx={{ px: '16px' }}
      >
        {time === ANYTIME ? (
          <AllTimeSlots
            slots={data.slots}
            handleSlotClick={handleSlotClick}
            name={name}
          />
        ) : (
          <ClosestAvailableTimeSlots
            slots={data.slots}
            handleSlotClick={handleSlotClick}
            time={time}
            name={name}
          />
        )}
        <DinerFieldsDrawer
          open={isFormDrawerOpen}
          handleFormDrawerClose={handleModalClose}
        />
      </Grid>
    </Grid>
  );
}

export default Availabilities;
